import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { LoadingSpinner } from "@/components/loading-spinner";
import { Button } from "@/components/ui/button";
import { ProtectByPermission } from "@/components/auth";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { formatCurrency } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { BidDashboard } from "@/components/bids/BidDashboard";
import {
  FileText,
  Files,
  Clock,
  Building2,
  DollarSign,
  Mail,
  Phone,
  Edit,
  Archive,
  Download,
  Share2,
  Printer,
  Users,
  MessageSquare,
  Settings,
  MapPin,
  Globe,
  Calculator,
  Briefcase,
  TrendingUp,
  CheckCircle,
  CheckCircle2,
  Calendar,
  Plus,
  ArrowLeft,
  Link,
  Trash2,
  Scale,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { EditBidSheet } from "./EditBidSheet";
import { AddBidTaskSheet } from "./AddBidTaskSheet";
import { EditBidTaskSheet } from "./EditBidTaskSheet";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";
import { PricingCalculator } from "./PricingCalculator";
import { CostTool } from "./CostTool";
import { WorkLoadingTool } from "./WorkLoadingTool";
import { ProfitAnalyzerTool } from "./ProfitAnalyzerTool";
import "@/styles/proposal.css";

import { InteractiveProposalsTab } from "@/components/bids/InteractiveProposalsTab";
import { ProposalSettingsSheet } from "@/components/bids/ProposalSettingsSheet";
import { ServiceAgreementSettingsSheet } from "@/components/bids/ServiceAgreementSettingsSheet";
import { BidServiceAgreementsTab } from "@/components/bids/BidServiceAgreementsTab";
import { QuickAutomationModal } from "@/components/workflows/QuickAutomationModal";
import { EmptyState } from "@/components/ui/empty-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function BidView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isProposalSettingsOpen, setIsProposalSettingsOpen] = useState(false);
  const [isAgreementSettingsOpen, setIsAgreementSettingsOpen] = useState(false);
  const [isAddTaskSheetOpen, setIsAddTaskSheetOpen] = useState(false);
  const [isEditTaskSheetOpen, setIsEditTaskSheetOpen] = useState(false);
  const [isAutomationModalOpen, setIsAutomationModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [newNote, setNewNote] = useState("");
  const [isManualMode, setIsManualMode] = useState(false);
  const [isWeeklyManualMode, setIsWeeklyManualMode] = useState(false);
  const [isMonthlyManualMode, setIsMonthlyManualMode] = useState(false);
  const [isQuarterlyManualMode, setIsQuarterlyManualMode] = useState(false);
  const [isManualPriceMode, setIsManualPriceMode] = useState(false);
  const [manualPrice, setManualPrice] = useState("");
  const [industryConsiderations, setIndustryConsiderations] = useState<string[]>([]);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [serviceList, setServiceList] = useState<
    Array<{
      id: string;
      service: string;
      description: string;
      frequency: string;
      serviceDays: string;
      monthlyPrice: string;
    }>
  >([]);
  const [newConsideration, setNewConsideration] = useState("");

  const [areaName, setAreaName] = useState("");
  const [weeklyAreaName, setWeeklyAreaName] = useState("");
  const [monthlyAreaName, setMonthlyAreaName] = useState("");
  const [quarterlyAreaName, setQuarterlyAreaName] = useState("");

  const [serviceDescriptions, setServiceDescriptions] = useState<string[]>([""]);
  const [weeklyServiceDescriptions, setWeeklyServiceDescriptions] = useState<string[]>([""]);
  const [monthlyServiceDescriptions, setMonthlyServiceDescriptions] = useState<string[]>([""]);
  const [quarterlyServiceDescriptions, setQuarterlyServiceDescriptions] = useState<string[]>([""]);

  const [areas, setAreas] = useState<{ name: string; services: string[] }[]>([]);
  const [weeklyAreas, setWeeklyAreas] = useState<{ name: string; services: string[] }[]>([]);
  const [monthlyAreas, setMonthlyAreas] = useState<{ name: string; services: string[] }[]>([]);
  const [quarterlyAreas, setQuarterlyAreas] = useState<{ name: string; services: string[] }[]>([]);
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "pricing"
    | "cost"
    | "workLoading"
    | "profitAnalyzer"
    | "proposal"
    | "agreements"
    | "tasks"
    | "notes"
  >("overview");

  // State for tracking which proposal tab should be active
  const [activeProposalTab, setActiveProposalTab] = useState<string>("cover-page");

  // State for tracking where user came from for dynamic back navigation
  const [backPath, setBackPath] = useState<string>("/bids");
  const [backLabel, setBackLabel] = useState<string>("Back to Bids");

  // State for status update
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showCreditAlert, setShowCreditAlert] = useState(false);

  // Fetch usage data
  const usage = useQuery(api.usage.getUsage);

  // Determine back navigation based on referrer or location state
  useEffect(() => {
    // Check if we have state from navigation (preferred method)
    if (location.state?.from) {
      const fromPath = location.state.from;
      if (fromPath.startsWith("/leads/")) {
        setBackPath(fromPath);
        setBackLabel("Back to Lead");
      } else if (fromPath.startsWith("/clients/")) {
        setBackPath(fromPath);
        setBackLabel("Back to Client");
      } else if (fromPath === "/dashboard") {
        setBackPath("/dashboard");
        setBackLabel("Back to Dashboard");
      } else if (fromPath === "/bids/archived") {
        setBackPath("/bids/archived");
        setBackLabel("Back to Archived Bids");
      } else {
        setBackPath("/bids");
        setBackLabel("Back to Bids");
      }
    } else {
      // Fallback: check document.referrer
      const referrer = document.referrer;
      if (referrer) {
        const referrerUrl = new URL(referrer);
        const referrerPath = referrerUrl.pathname;

        if (referrerPath.startsWith("/leads/")) {
          setBackPath(referrerPath);
          setBackLabel("Back to Lead");
        } else if (referrerPath.startsWith("/clients/")) {
          setBackPath(referrerPath);
          setBackLabel("Back to Client");
        } else if (referrerPath === "/dashboard") {
          setBackPath("/dashboard");
          setBackLabel("Back to Dashboard");
        } else if (referrerPath === "/bids/archived") {
          setBackPath("/bids/archived");
          setBackLabel("Back to Archived Bids");
        } else if (referrerPath.startsWith("/bids")) {
          setBackPath("/bids");
          setBackLabel("Back to Bids");
        }
      }
      // If no referrer or unrecognized path, keep default (/bids)
    }
  }, [location.state]);

  // Fetch bid data from Convex
  const bid = useQuery(api.bids.getBidById, id ? { id: id as Id<"bids"> } : "skip");

  // Fetch client's business information if bid has a businessId
  const business = useQuery(
    api.businesses.getBusinessById,
    bid?.businessId ? { id: bid.businessId } : "skip"
  );

  // Fetch the current user's organization (for branding, logos, contact info)
  const organization = useQuery(api.organizations.getByUser);

  // Fetch proposal data for this bid
  const proposal = useQuery(api.bidProposals.getByBidId, id ? { bidId: id as Id<"bids"> } : "skip");

  // Fetch tasks for this bid
  const bidTasks =
    useQuery(api.bidTasks.getTasksByBid, id ? { bidId: id as Id<"bids"> } : "skip") || [];

  // Fetch notes for this bid
  const bidNotes =
    useQuery(api.bidNotes.getNotesByBid, id ? { bidId: id as Id<"bids"> } : "skip") || [];

  // Fetch service location if bid is linked to one
  const serviceLocation = useQuery(
    api.serviceLocations.getLocationById,
    bid?.serviceLocationId ? { id: bid.serviceLocationId } : "skip"
  );

  // Create note mutation
  const createNote = useMutation(api.bidNotes.createNote);

  // Update task mutation
  const updateTask = useMutation(api.bidTasks.updateTask);

  // Delete task mutation
  const deleteTask = useMutation(api.bidTasks.deleteTask);

  // Delete note mutation
  const deleteNote = useMutation(api.bidNotes.deleteNote);

  // Archive mutation
  const archiveBid = useMutation(api.bids.archive);

  // Status update mutation
  const updateBidStatus = useMutation(api.bids.update);

  // Confetti animation function
  const triggerConfetti = () => {
    // First burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#10B981", "#059669", "#047857", "#065F46", "#FFD700", "#FFA500"],
    });

    // Second burst with delay
    setTimeout(() => {
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 },
        colors: ["#10B981", "#059669", "#047857", "#065F46", "#FFD700", "#FFA500"],
      });
    }, 250);
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus: string) => {
    if (!bid || newStatus === bid.status) return;

    const previousStatus = bid.status;
    setIsUpdatingStatus(true);

    try {
      let updatedValue = bid.value; // Keep current value by default
      let updatedApprovalDate = bid.approvalDate; // Keep current approval date by default

      // Calculate value and approval date based on status change
      if (newStatus === "Approved" && previousStatus !== "Approved") {
        // Status changing TO Approved - set value from bidSummary and set approval date
        if (bidSummary?.recommendedMonthlyPrice) {
          updatedValue = formatCurrency(bidSummary.recommendedMonthlyPrice);
        } else {
          console.warn("No recommended monthly price available for approved bid");
          updatedValue = ""; // Clear value if no pricing data available
        }
        updatedApprovalDate = Date.now(); // Set approval date to current timestamp
      } else if (previousStatus === "Approved" && newStatus !== "Approved") {
        // Status changing FROM Approved to something else - clear value and approval date
        updatedValue = "";
        updatedApprovalDate = undefined; // Clear approval date
      }

      // Update bid with new status, calculated value, and approval date
      await updateBidStatus({
        id: id as Id<"bids">,
        status: newStatus as "Draft" | "Submitted" | "Approved" | "Rejected" | "In Review",
        value: updatedValue,
        approvalDate: updatedApprovalDate,
      });

      // Trigger confetti if status changed to Approved
      if (newStatus === "Approved" && previousStatus !== "Approved") {
        triggerConfetti();
        const approvalDateStr = new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        toast.success("🎉 Congratulations! Bid has been approved!", {
          description: `Your bid has been successfully approved on ${approvalDateStr} with a value of ${updatedValue || "TBD"}.`,
          duration: 5000,
        });
      } else {
        toast.success(`Bid status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error("Failed to update bid status:", error);
      toast.error("Failed to update bid status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Fetch bid pricing data
  const bidServices = useQuery(api.bidPricing.getByBidId, {
    bidId: id as Id<"bids">,
  });

  // Fetch bid summary
  const bidSummary = useQuery(api.bidPricing.getBidSummary, {
    bidId: id as Id<"bids">,
  });

  // Fetch cost summary
  const costSummary = useQuery(api.bidCostSummaries.getBidCostSummary, {
    bidId: id as Id<"bids">,
  });

  // Fetch profit analysis
  const profitAnalysis = useQuery(api.bidProfitAnalysis.getBidProfitAnalysis, {
    bidId: id as Id<"bids">,
  });

  // Function to generate proposal HTML from proposal data
  const generateProposalHtml = (proposalData: any) => {
    if (!proposalData) return null;

    const companyName = proposalData.coverPage?.companyName || "Your Company";
    const currentDate = new Date().toLocaleDateString();

    // Generate HTML for the proposal based on the saved data
    return `
      <div class="proposal-content">
        <!-- Cover Page -->
        <div class="proposal-page">
          <div class="proposal-content">
            <div class="cover-page">
              <div class="mb-8">
                <h1 class="text-4xl font-bold janitorial-blue mb-2">${proposalData.coverPage?.proposalTitle || bid.title}</h1>
                <h2 class="text-5xl font-bold janitorial-green mb-8">PROPOSAL</h2>
                <h3 class="text-2xl font-medium mb-2">Prepared for: ${proposalData.coverPage?.clientName || business?.contactName || bid.client.contactName}</h3>
                <p class="text-lg text-gray-600">${proposalData.coverPage?.clientCompany || business?.name || bid.client.company}</p>
              </div>
              <div class="mt-16 mb-12">
                <h1 class="text-6xl font-bold">${companyName}</h1>
                <p class="text-xl text-gray-600 mt-2">${proposalData.coverPage?.companyTagline || "Professional Cleaning Services"}</p>
              </div>
              <div class="mt-16 text-gray-500">
                <p>Proposal Date: ${proposalData.coverPage?.proposalDate || currentDate}</p>
                <p>Valid Until: ${proposalData.coverPage?.validUntilDate || ""}</p>
                <p class="mt-8">© ${new Date().getFullYear()} ${companyName}. All rights reserved.</p>
                <p>CONFIDENTIAL: For client use only</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Executive Summary Page -->
        <div class="proposal-page">
          <!-- Header -->
          <div class="proposal-header">
            <div class="flex justify-between items-center">
              <div>
                <h3 class="text-lg font-bold janitorial-blue">${companyName}</h3>
                <p class="text-sm text-gray-600">Professional Cleaning Services</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-600">Proposal for: ${bid.client.company}</p>
                <p class="text-sm text-gray-600">Date: ${currentDate}</p>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="proposal-content">
            <div class="section">
            <h2 class="text-2xl font-bold janitorial-blue mb-4">Executive Summary</h2>
            <p class="mb-4">
              ${
                proposalData.executiveSummary?.companyIntroduction ||
                `${companyName} is pleased to present this proposal for comprehensive cleaning services for ${bid.client.company}.`
              }
            </p>
            <p class="mb-4">
              ${
                proposalData.executiveSummary?.proposedSolution ||
                "We understand your facility requires professional cleaning services that maintain a healthy, presentable environment while being cost-effective."
              }
            </p>

            <h3 class="text-xl font-bold janitorial-green mb-2 mt-6">Key Benefits</h3>
            <ul class="list-disc pl-6 mb-6">
              ${
                proposalData.executiveSummary?.keyBenefits
                  ? proposalData.executiveSummary.keyBenefits
                      .map(
                        (benefit: any) =>
                          `<li class="mb-2"><strong>${benefit.title}</strong>: ${benefit.description}</li>`
                      )
                      .join("")
                  : `<li class="mb-2"><strong>Quality Assurance</strong>: Rigorous quality control processes</li>
                 <li class="mb-2"><strong>Experienced Staff</strong>: Well-trained cleaning professionals</li>
                 <li class="mb-2"><strong>Customized Service</strong>: Tailored to your specific needs</li>`
              }
            </ul>
            </div>
          </div>

          <!-- Footer -->
          <div class="proposal-footer">
            <div class="flex justify-between">
              <span>${companyName} - Confidential</span>
              <span>Page 2</span>
            </div>
          </div>
        </div>

        <!-- Service Schedule Page -->
        <div class="proposal-page">
          <!-- Header -->
          <div class="proposal-header">
            <div class="flex justify-between items-center">
              <div>
                <h3 class="text-lg font-bold janitorial-blue">${companyName}</h3>
                <p class="text-sm text-gray-600">Professional Cleaning Services</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-600">Proposal for: ${bid.client.company}</p>
                <p class="text-sm text-gray-600">Date: ${currentDate}</p>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="proposal-content">
            <div class="section">
            <h2 class="text-2xl font-bold janitorial-blue mb-4">Service Schedule</h2>
            <p class="mb-4">
              Our proposed cleaning schedule is designed to meet the specific needs of your facility while minimizing disruption to your operations.
            </p>

            <h3 class="text-xl font-bold janitorial-green mb-2 mt-6">Cleaning Frequency</h3>
            <p class="mb-4">
              ${
                proposalData.serviceSchedule?.serviceFrequency === "5_per_week"
                  ? "Five days per week (Monday-Friday)"
                  : proposalData.serviceSchedule?.serviceFrequency === "7_per_week"
                    ? "Seven days per week"
                    : proposalData.serviceSchedule?.serviceFrequency === "3_per_week"
                      ? "Three days per week"
                      : "Five days per week (Monday-Friday)"
              }
            </p>

            <h3 class="text-xl font-bold janitorial-green mb-2 mt-6">Services Included</h3>
            <div class="mb-4">
              ${
                proposalData.serviceSchedule?.dailyServices &&
                proposalData.serviceSchedule.dailyServices.length > 0
                  ? `<h4 class="text-lg font-semibold mb-2">Daily Services</h4>
                 <ul class="list-disc pl-6 mb-4">
                   ${proposalData.serviceSchedule.dailyServices
                     .map(
                       (area: any) =>
                         `<li class="mb-2"><strong>${area.name}</strong>:
                       <ul class="list-disc pl-6">
                         ${area.services.map((service: string) => `<li>${service}</li>`).join("")}
                       </ul>
                     </li>`
                     )
                     .join("")}
                 </ul>`
                  : ""
              }

              ${
                proposalData.serviceSchedule?.weeklyServices &&
                proposalData.serviceSchedule.weeklyServices.length > 0
                  ? `<h4 class="text-lg font-semibold mb-2">Weekly Services</h4>
                 <ul class="list-disc pl-6 mb-4">
                   ${proposalData.serviceSchedule.weeklyServices
                     .map(
                       (area: any) =>
                         `<li class="mb-2"><strong>${area.name}</strong>:
                       <ul class="list-disc pl-6">
                         ${area.services.map((service: string) => `<li>${service}</li>`).join("")}
                       </ul>
                     </li>`
                     )
                     .join("")}
                 </ul>`
                  : ""
              }
            </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="proposal-footer">
            <div class="flex justify-between">
              <span>${companyName} - Confidential</span>
              <span>Page 3</span>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  // Function to generate preview HTML when no proposal has been saved
  const generatePreviewHtml = () => {
    const companyName = "Your Company";
    const currentDate = new Date().toLocaleDateString();

    return `
      <div class="proposal-content">
        <!-- Cover Page -->
        <div class="proposal-page">
          <div class="proposal-content">
            <div class="cover-page">
              <div class="mb-8">
                <h1 class="text-4xl font-bold janitorial-blue mb-2">${bid.title}</h1>
                <h2 class="text-5xl font-bold janitorial-green mb-8">PROPOSAL</h2>
                <h3 class="text-2xl font-medium mb-2">Prepared for: ${bid.client.contactName}</h3>
              </div>
              <div class="mt-16 mb-12">
                <h1 class="text-6xl font-bold">${companyName}</h1>
                <p class="text-xl text-gray-600 mt-2">Professional Cleaning Services</p>
              </div>
              <div class="mt-16 text-gray-500">
                <p>Proposal Date: ${currentDate}</p>
                <div class="price-callout mt-8">
                  <h4 class="text-xl font-bold mb-2 janitorial-blue">Preview Mode</h4>
                  <p>This is a preview of your proposal. Go to the Proposal Details tab to create a complete proposal.</p>
                </div>
                <p class="mt-8">© ${new Date().getFullYear()} ${companyName}. All rights reserved.</p>
                <p>CONFIDENTIAL: For client use only</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview Page 2 -->
        <div class="proposal-page">
          <!-- Header -->
          <div class="proposal-header">
            <div class="flex justify-between items-center">
              <div>
                <h3 class="text-lg font-bold janitorial-blue">${companyName}</h3>
                <p class="text-sm text-gray-600">Professional Cleaning Services</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-600">Proposal for: ${bid.client.company}</p>
                <p class="text-sm text-gray-600">Date: ${currentDate}</p>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="proposal-content">
            <div class="section">
              <h2 class="text-2xl font-bold janitorial-blue mb-4">Executive Summary</h2>
              <p class="mb-4">
                This is a preview of the executive summary section. In the full proposal, this section will contain
                information about our company and the proposed cleaning services for ${bid.client.company}.
              </p>

              <div class="price-callout mt-8">
                <h4 class="text-xl font-bold mb-2 janitorial-blue">Preview Mode</h4>
                <p>This is a preview of your proposal. Go to the Proposal Details tab to create a complete proposal.</p>
              </div>

              <h3 class="text-xl font-bold janitorial-green mb-2 mt-6">Key Benefits</h3>
              <ul class="list-disc pl-6 mb-6">
                <li class="mb-2"><strong>Quality Assurance</strong>: Rigorous quality control processes</li>
                <li class="mb-2"><strong>Experienced Staff</strong>: Well-trained cleaning professionals</li>
                <li class="mb-2"><strong>Customized Service</strong>: Tailored to your specific needs</li>
              </ul>
            </div>
          </div>

          <!-- Footer -->
          <div class="proposal-footer">
            <div class="flex justify-between">
              <span>${companyName} - Confidential</span>
              <span>Page 2</span>
            </div>
          </div>
        </div>

        <!-- Preview Page 3 -->
        <div class="proposal-page">
          <!-- Header -->
          <div class="proposal-header">
            <div class="flex justify-between items-center">
              <div>
                <h3 class="text-lg font-bold janitorial-blue">${companyName}</h3>
                <p class="text-sm text-gray-600">Professional Cleaning Services</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-600">Proposal for: ${bid.client.company}</p>
                <p class="text-sm text-gray-600">Date: ${currentDate}</p>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="proposal-content">
            <div class="section">
              <h2 class="text-2xl font-bold janitorial-blue mb-4">Service Schedule</h2>
              <p class="mb-4">
                This is a preview of the service schedule section. In the full proposal, this section will detail
                the cleaning schedule and services to be provided for ${bid.client.company}.
              </p>

              <div class="price-callout mt-8">
                <h4 class="text-xl font-bold mb-2 janitorial-blue">Preview Mode</h4>
                <p>This is a preview of your proposal. Go to the Proposal Details tab to create a complete proposal.</p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="proposal-footer">
            <div class="flex justify-between">
              <span>${companyName} - Confidential</span>
              <span>Page 3</span>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  // Show loading state while fetching data
  if (!bid) {
    return (
      <DashboardLayout activeTab="bids">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  const handleAddArea = () => {
    if (areaName.trim() === "") return;

    // Filter out empty service descriptions
    const filteredServices = serviceDescriptions.filter((desc) => desc.trim() !== "");

    if (filteredServices.length === 0) return;

    setAreas([...areas, { name: areaName, services: filteredServices }]);

    // Reset form
    setAreaName("");
    setServiceDescriptions([""]);
  };

  const handleAddWeeklyArea = () => {
    if (weeklyAreaName.trim() === "") return;

    // Filter out empty service descriptions
    const filteredServices = weeklyServiceDescriptions.filter((desc) => desc.trim() !== "");

    if (filteredServices.length === 0) return;

    setWeeklyAreas([...weeklyAreas, { name: weeklyAreaName, services: filteredServices }]);

    // Reset form
    setWeeklyAreaName("");
    setWeeklyServiceDescriptions([""]);
  };

  const handleAddMonthlyArea = () => {
    if (monthlyAreaName.trim() === "") return;

    // Filter out empty service descriptions
    const filteredServices = monthlyServiceDescriptions.filter((desc) => desc.trim() !== "");

    if (filteredServices.length === 0) return;

    setMonthlyAreas([...monthlyAreas, { name: monthlyAreaName, services: filteredServices }]);

    // Reset form
    setMonthlyAreaName("");
    setMonthlyServiceDescriptions([""]);
  };

  const handleAddQuarterlyArea = () => {
    if (quarterlyAreaName.trim() === "") return;

    // Filter out empty service descriptions
    const filteredServices = quarterlyServiceDescriptions.filter((desc) => desc.trim() !== "");

    if (filteredServices.length === 0) return;

    setQuarterlyAreas([...quarterlyAreas, { name: quarterlyAreaName, services: filteredServices }]);

    // Reset form
    setQuarterlyAreaName("");
    setQuarterlyServiceDescriptions([""]);
  };

  // Handle adding a new note
  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;

    try {
      await createNote({
        bidId: id as Id<"bids">,
        content: newNote,
      });
      setNewNote("");
    } catch (err) {
      console.error("Error creating note:", err);
    }
  };

  // Handle toggling task status
  const handleToggleTaskStatus = async (taskId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === "completed" ? "pending" : "completed";
      await updateTask({
        id: taskId as any,
        status: newStatus,
      });
    } catch (err) {
      console.error("Error updating task:", err);
    }
  };

  // Handle archive bid
  const handleArchive = async () => {
    if (!bid) return;

    try {
      await archiveBid({ id: bid._id });
      toast.success(`${bid.title} has been archived successfully`);
      navigate("/bids");
    } catch (error) {
      console.error("Failed to archive bid:", error);
      toast.error("Failed to archive bid");
    }
  };

  return (
    <DashboardLayout activeTab="bids">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1"
                onClick={() => navigate(backPath)}
              >
                <ArrowLeft className="h-4 w-4" /> {backLabel}
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{bid.title}</h1>
            <div className="mt-1 flex items-center gap-3">
              <p className="text-gray-500">
                Last modified: {new Date(bid.lastModified).toLocaleDateString()}
              </p>
              {serviceLocation && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                  <Link className="h-3 w-3" />
                  {serviceLocation.name}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-3 flex-wrap justify-end">

            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                if (
                  usage &&
                  usage.creditsUsed >= usage.creditLimit &&
                  (usage.purchasedCredits || 0) <= 0
                ) {
                  setShowCreditAlert(true);
                } else {
                  setIsProposalSettingsOpen(true);
                }
              }}
            >
              <Files className="h-4 w-4" /> Create a Proposal
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                if (
                  usage &&
                  usage.creditsUsed >= usage.creditLimit &&
                  (usage.purchasedCredits || 0) <= 0
                ) {
                  setShowCreditAlert(true);
                } else {
                  setIsAgreementSettingsOpen(true);
                }
              }}
            >
              <Scale className="h-4 w-4" /> Create Service Agreement
            </Button>
            <ProtectByPermission permission="bids:write">
              <Button variant="outline" className="gap-2" onClick={handleArchive}>
                <Archive className="h-4 w-4" /> Archive
              </Button>
              <Button className="gap-2" onClick={() => setIsEditSheetOpen(true)}>
                <Edit className="h-4 w-4" /> Edit Bid Details
              </Button>
            </ProtectByPermission>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex space-x-8">
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "overview"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "pricing"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("pricing")}
              >
                Pricing
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "cost"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("cost")}
              >
                Cost
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "workLoading"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("workLoading")}
              >
                Work Loading
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "profitAnalyzer"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("profitAnalyzer")}
              >
                Profit Analyzer
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "proposal"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("proposal")}
              >
                Proposals
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "agreements"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("agreements")}
              >
                Agreements
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "tasks"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("tasks")}
              >
                Tasks
              </button>
              <button
                className={`border-b-2 px-1 py-4 text-sm font-medium ${
                  activeTab === "notes"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("notes")}
              >
                Notes
              </button>
            </div>

            {/* Status Dropdown */}
            <div className="flex items-center py-2">
              <ProtectByPermission permission="bids:write">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Status:</label>
                  <Select
                    value={bid.status}
                    onValueChange={handleStatusUpdate}
                    disabled={isUpdatingStatus}
                  >
                    <SelectTrigger className={`h-8 w-36 ${isUpdatingStatus ? "opacity-50" : ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Draft">
                        <span className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                          Draft
                        </span>
                      </SelectItem>
                      <SelectItem value="Submitted">
                        <span className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                          Submitted
                        </span>
                      </SelectItem>
                      <SelectItem value="In Review">
                        <span className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                          In Review
                        </span>
                      </SelectItem>
                      <SelectItem value="Approved">
                        <span className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-500"></div>
                          Approved
                        </span>
                      </SelectItem>
                      <SelectItem value="Rejected">
                        <span className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-red-500"></div>
                          Rejected
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {isUpdatingStatus && (
                    <span className="ml-1 text-xs text-gray-500">Updating...</span>
                  )}
                </div>
              </ProtectByPermission>
            </div>
          </div>
        </div>

        {activeTab === "overview" && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Left Column - Details */}
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Bid Details</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FileText className="h-4 w-4" />
                    <span>Status: </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        bid.status === "Approved"
                          ? "bg-green-100 text-green-700"
                          : bid.status === "Submitted"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {bid.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <FileText className="h-4 w-4" />
                    <span>Description: {bid.description || "No description provided"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>Created: {new Date(bid.createdAt).toLocaleDateString()}</span>
                  </div>
                  {bid.approvalDate && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span>
                        Approved:{" "}
                        {new Date(bid.approvalDate).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Client Information</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                      <Building2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">{business?.name || bid.client.company}</h3>
                    </div>
                  </div>

                  <Separator />

                  {(business?.address || bid.client.address) && (
                    <>
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">Address</h3>
                        <div className="flex items-center gap-2 text-gray-700">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span>{business?.address || bid.client.address}</span>
                        </div>
                      </div>
                      <Separator />
                    </>
                  )}

                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Contact Information</h3>
                    <div className="space-y-2">
                      {(business?.contactName || bid.client.contactName) && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span>
                            {business?.contactName || bid.client.contactName}
                            {(business?.contactTitle || bid.client.title) &&
                              ` - ${business?.contactTitle || bid.client.title}`}
                          </span>
                        </div>
                      )}
                      {(business?.contactEmail || bid.client.email) && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <a
                            href={`mailto:${business?.contactEmail || bid.client.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {business?.contactEmail || bid.client.email}
                          </a>
                        </div>
                      )}
                      {(business?.contactPhone || bid.client.phone) && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span>{business?.contactPhone || bid.client.phone}</span>
                        </div>
                      )}
                      {business?.website && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Globe className="h-4 w-4 text-gray-400" />
                          <a
                            href={
                              business.website.startsWith("http")
                                ? business.website
                                : `https://${business.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {business.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column - Bid Dashboard */}
            <BidDashboard
              bid={bid}
              bidServices={bidServices || []}
              bidSummary={bidSummary}
              costSummary={costSummary}
              profitAnalysis={profitAnalysis}
            />
          </div>
        )}

        {activeTab === "pricing" && (
          <div className="space-y-6">
            <PricingCalculator bidId={id as Id<"bids">} />
          </div>
        )}

        {activeTab === "cost" && (
          <div className="space-y-6">
            <CostTool
              bidId={id as Id<"bids">}
              onSave={(data) => {
                console.log("Cost data saved:", data);
                // Here you can handle the saved cost data
              }}
            />
          </div>
        )}

        {activeTab === "workLoading" && (
          <div className="space-y-6">
            <WorkLoadingTool />
          </div>
        )}

        {activeTab === "profitAnalyzer" && (
          <div className="space-y-6">
            <ProfitAnalyzerTool
              bidId={id as Id<"bids">}
              onSave={(data) => {
                console.log("Profit analysis data saved:", data);
                // Here you can handle the saved profit analysis data
                // Note: Toast notification is handled by ProfitAnalyzerTool component
              }}
            />
          </div>
        )}

        {activeTab === "proposal" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Proposals</CardTitle>
                <CardDescription>Manage and view proposals generated for this bid.</CardDescription>
              </CardHeader>
              <CardContent>
                <InteractiveProposalsTab bidId={id as Id<"bids">} />
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "agreements" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Service Agreements</CardTitle>
                <CardDescription>
                  Manage and view service agreements generated for this bid.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BidServiceAgreementsTab bidId={id as Id<"bids">} />
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "tasks" && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Tasks</h2>
                  <ProtectByPermission permission="bids:write">
                    <Button size="sm" className="gap-2" onClick={() => setIsAddTaskSheetOpen(true)}>
                      <Plus className="h-4 w-4" /> Add Task
                    </Button>
                  </ProtectByPermission>
                </div>
                {bidTasks.length === 0 ? (
                  <EmptyState
                    icon={CheckCircle2}
                    title="No tasks yet"
                    description="Add a task to get started."
                  />
                ) : (
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-4">
                      {bidTasks.map(
                        (task: {
                          _id: Id<"bidTasks">;
                          title: string;
                          status: string;
                          priority?: string;
                          dueDate: string;
                          createdByName?: string;
                          assignedToName: string;
                        }) => (
                          <Card key={task._id} className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  {task.priority && (
                                    <span
                                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                        {
                                          low: "bg-blue-100 text-blue-700",
                                          medium: "bg-yellow-100 text-yellow-700",
                                          high: "bg-red-100 text-red-700",
                                        }[task.priority as "low" | "medium" | "high"] ||
                                        "bg-blue-100 text-blue-700"
                                      }`}
                                    >
                                      {task.priority.charAt(0).toUpperCase() +
                                        task.priority.slice(1)}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handleToggleTaskStatus(task._id, task.status)}
                                    className="focus:outline-none"
                                  >
                                    <CheckCircle2
                                      className={`h-4 w-4 ${task.status === "completed" ? "text-green-500" : "text-gray-400"}`}
                                    />
                                  </button>
                                  <span className="font-medium">{task.title}</span>
                                </div>
                                <div className="flex flex-col gap-1 text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {task.dueDate}
                                  </div>
                                  {task.createdByName && (
                                    <div className="flex items-center gap-1">
                                      <span>Created by</span>
                                      <Users className="h-3 w-3" />
                                      <span>{task.createdByName}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1">
                                    <span>Assigned to</span>
                                    <Users className="h-3 w-3" />
                                    <span>{task.assignedToName}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    setSelectedTask(task);
                                    setIsEditTaskSheetOpen(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                  <span className="sr-only">Edit</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => deleteTask({ id: task._id })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                  <span className="sr-only">Delete</span>
                                </Button>
                              </div>
                            </div>
                          </Card>
                        )
                      )}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </Card>
          </div>
        )}

        {activeTab === "notes" && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="space-y-4">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Notes</h2>
                </div>
                <div className="space-y-4">
                  <Card className="p-4">
                    <Textarea
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="mb-4"
                    />
                    <Button className="gap-2" onClick={handleAddNote}>
                      <Plus className="h-4 w-4" /> Add Note
                    </Button>
                  </Card>
                  {bidNotes.length === 0 ? (
                    <EmptyState
                      icon={MessageSquare}
                      title="No notes yet"
                      description="Add a note to get started."
                    />
                  ) : (
                    <ScrollArea className="h-[300px] pr-4">
                      <div className="space-y-4">
                        {bidNotes.map(
                          (note: {
                            _id: Id<"bidNotes">;
                            content: string;
                            createdAt: number;
                            createdBy?: string;
                            createdByName: string;
                          }) => (
                            <Card key={note._id} className="p-4">
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <p className="text-gray-600">{note.content}</p>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => deleteNote({ id: note._id })}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M3 6h18"></path>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                  </Button>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <span>Created by</span>
                                  <Users className="h-3 w-3" />
                                  <span>{note.createdByName || note.createdBy || "Unknown"}</span>
                                  <span>•</span>
                                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                                </div>
                              </div>
                            </Card>
                          )
                        )}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
      <EditBidSheet isOpen={isEditSheetOpen} onClose={() => setIsEditSheetOpen(false)} bid={bid} />
      <AddBidTaskSheet
        isOpen={isAddTaskSheetOpen}
        onClose={() => setIsAddTaskSheetOpen(false)}
        bidId={id as Id<"bids">}
        onTaskAdded={() => {
          // Force a re-render to refresh tasks
        }}
      />
      {selectedTask && (
        <EditBidTaskSheet
          isOpen={isEditTaskSheetOpen}
          onClose={() => setIsEditTaskSheetOpen(false)}
          task={selectedTask}
          onTaskUpdated={() => {
            // Force a re-render to refresh tasks
          }}
        />
      )}
      {/* Proposal Settings Sheet */}
      <ProposalSettingsSheet
        isOpen={isProposalSettingsOpen}
        onClose={() => setIsProposalSettingsOpen(false)}
        bidId={id}
        businessId={bid.businessId}
        clientName={bid.client.company}
        bidDataContext={{
          services: bidServices,
          summary: bidSummary,
          cost: costSummary,
          location: serviceLocation,
          description: bid.description,
          client: bid.client,
          business: business, // Client's business info
          organization: organization, // User's organization info (branding, logo)
        }}
      />
      <ServiceAgreementSettingsSheet
        isOpen={isAgreementSettingsOpen}
        onClose={() => setIsAgreementSettingsOpen(false)}
        bidId={id}
        businessId={bid.businessId}
        clientName={bid.client.company}
        monthlyPrice={bidSummary?.recommendedMonthlyPrice}
      />

      <UpgradePromptDialog
        isOpen={showCreditAlert}
        onClose={() => setShowCreditAlert(false)}
        title="Upgrade to Starter"
        description="Access AI Proposals and Service Agreements to generate professional client documents in seconds."
        feature="AI Document Generation"
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />

      <QuickAutomationModal
        isOpen={isAutomationModalOpen}
        onClose={() => setIsAutomationModalOpen(false)}
        context="bid"
        recordId={id as string}
      />
    </DashboardLayout>
  );
}
